/* SCI x FW Discovery Set — hero sand unveil + split-flap countdown.

   No third-party libraries. The scroll scrub is a requestAnimationFrame
   sampler over getBoundingClientRect() and the flip runs on the Web
   Animations API, because inside the Shopify theme editor iframe neither a
   CDN script nor a one-shot DOMContentLoaded bootstrap can be relied on: the
   editor injects re-rendered section markup into an already-parsed document
   and never re-executes the section's own <script> tags. Everything below is
   therefore idempotent and re-mountable from the section lifecycle events. */
(() => {
  const NS = (window.sciLanding = window.sciLanding || { live: new Map(), wired: false });

  function clamp(v, a = 0, b = 1) {
    return Math.max(a, Math.min(b, v));
  }
  function ease(t) {
    t = clamp(t);
    return 1 - Math.pow(1 - t, 3);
  }
  function easeInOut(t) {
    t = clamp(t);
    return t < 0.5 ? 4 * t * t * t : 1 - Math.pow(-2 * t + 2, 3) / 2;
  }
  function seg(p, a, b) {
    return clamp((p - a) / (b - a));
  }
  function mix(a, b, t) {
    return a + (b - a) * t;
  }

  /* Measured off the source plates — see the header of the stylesheet.
     FRAME_ASPECT is 2752/1536; CROP is the window shared by
     sci-wide-clean.jpg and sci-sand-map.png. */
  const FRAME_ASPECT = 2752 / 1536;
  const FOCUS = { x: 0.502, y: 0.478 };
  const CROP = { x0: 0.335, y0: 0.14, x1: 0.672, y1: 0.835 };
  const CROP_W = CROP.x1 - CROP.x0;
  const CROP_H = CROP.y1 - CROP.y0;

  /* The sampling window into the full frame, cover-fitted to the stage and
     centred on the emblem. `zoom` < 1 pushes the camera in. */
  function coverView(w, h, zoom) {
    const a = w / Math.max(h, 1);
    let sx;
    let sy;
    if (a >= FRAME_ASPECT) {
      sx = 1;
      sy = FRAME_ASPECT / a;
    } else {
      sx = a / FRAME_ASPECT;
      sy = 1;
    }
    sx = Math.min(1, sx * zoom);
    sy = Math.min(1, sy * zoom);
    return {
      sx,
      sy,
      ox: clamp(FOCUS.x - sx / 2, 0, 1 - sx),
      oy: clamp(FOCUS.y - sy / 2, 0, 1 - sy),
    };
  }

  const VERT = [
    "attribute vec2 aPos;",
    "varying vec2 vUv;",
    "void main(){",
    "  vUv = vec2(aPos.x * 0.5 + 0.5, 0.5 - aPos.y * 0.5);",
    "  gl_Position = vec4(aPos, 0.0, 1.0);",
    "}",
  ].join("\n");

  /* Noise-threshold dissolve. Every pixel of the carton carries its own
     lift-off time in the atlas red channel, so the sand erodes grain by
     grain instead of fading as a block. */
  const FRAG = [
    "precision mediump float;",
    "varying vec2 vUv;",
    "uniform sampler2D uPlate;",
    "uniform sampler2D uClean;",
    "uniform sampler2D uMap;",
    "uniform vec2 uScale;",
    "uniform vec2 uOffset;",
    "uniform vec2 uCropMin;",
    "uniform vec2 uCropSize;",
    "uniform vec2 uGrainCells;",
    "uniform float uProgress;",
    "uniform float uEdge;",
    "uniform float uDrift;",
    "uniform float uLift;",
    "float hash(vec2 p){",
    "  return fract(sin(dot(p, vec2(127.1, 311.7))) * 43758.5453123);",
    "}",
    "void main(){",
    "  vec2 fuv = vUv * uScale + uOffset;",
    "  vec2 auv = (fuv - uCropMin) / uCropSize;",
    "  if (auv.x < 0.0 || auv.x > 1.0 || auv.y < 0.0 || auv.y > 1.0) {",
    "    gl_FragColor = vec4(texture2D(uPlate, fuv).rgb, 1.0);",
    "    return;",
    "  }",
    "  vec4 m = texture2D(uMap, auv);",
    "  float region = m.g;",
    "  float grain = hash(floor(auv * uGrainCells));",
    "  float ero = clamp(m.r + (grain - 0.5) * 0.11, 0.0, 1.0);",
    // widen the sweep so progress 0 is fully buried and 1 is fully clear
    "  float p = uProgress * (1.0 + 2.0 * uEdge) - uEdge;",
    "  float cleared = (1.0 - smoothstep(p - uEdge, p + uEdge, ero)) * region;",
    // the transition band: 1 exactly where grains are lifting right now
    "  float front = clamp(cleared * (1.0 - cleared) * 4.0, 0.0, 1.0);",
    // sand slides up and to the right as it releases
    "  vec2 drift = vec2(0.85, -0.62) * uDrift * front;",
    "  vec3 sand = texture2D(uPlate, fuv + drift).rgb;",
    "  vec3 clean = texture2D(uClean, auv).rgb;",
    "  vec3 col = mix(sand, clean, cleared);",
    // warm dust catching the light along the erosion front
    "  col += vec3(0.60, 0.45, 0.20) * front * region * uLift * (0.35 + 0.65 * grain);",
    "  gl_FragColor = vec4(col, 1.0);",
    "}",
  ].join("\n");

  function compile(gl, type, src) {
    const sh = gl.createShader(type);
    gl.shaderSource(sh, src);
    gl.compileShader(sh);
    if (!gl.getShaderParameter(sh, gl.COMPILE_STATUS)) {
      gl.deleteShader(sh);
      return null;
    }
    return sh;
  }

  function makeTexture(gl, img) {
    const tex = gl.createTexture();
    gl.bindTexture(gl.TEXTURE_2D, tex);
    gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_WRAP_S, gl.CLAMP_TO_EDGE);
    gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_WRAP_T, gl.CLAMP_TO_EDGE);
    gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MIN_FILTER, gl.LINEAR);
    gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MAG_FILTER, gl.LINEAR);
    gl.texImage2D(gl.TEXTURE_2D, 0, gl.RGBA, gl.RGBA, gl.UNSIGNED_BYTE, img);
    return tex;
  }

  function ready(img) {
    return !!img && img.complete && img.naturalWidth > 0;
  }

  function whenReady(imgs, done) {
    const list = imgs.filter(Boolean);
    let left = list.length;
    if (!left) {
      done(false);
      return;
    }
    let failed = false;
    list.forEach((img) => {
      const tick = () => {
        left -= 1;
        if (left === 0) done(!failed);
      };
      if (ready(img)) {
        tick();
        return;
      }
      if (img.complete) {
        // decoded to nothing: a 404 that fired before this listener attached
        failed = true;
        tick();
        return;
      }
      img.addEventListener("load", tick, { once: true });
      img.addEventListener(
        "error",
        () => {
          failed = true;
          // eslint-disable-next-line no-console
          console.warn("[sci-landing] hero plate failed to load:", img.currentSrc || img.src);
          tick();
        },
        { once: true }
      );
    });
  }

  const CAN_WAAPI = typeof Element !== "undefined" && typeof Element.prototype.animate === "function";
  const FLIP_IN = "cubic-bezier(0.55, 0.085, 0.68, 0.53)";
  const FLIP_OUT = "cubic-bezier(0.25, 0.46, 0.45, 0.94)";

  function create(root) {
    const hero = root.querySelector("[data-sci-hero]");
    const cue = root.querySelector("[data-sci-cue]");
    const caption = root.querySelector("[data-sci-caption]");
    const timer = root.querySelector("[data-sci-countdown]");
    const kicker = root.querySelector("[data-sci-count-kicker]");
    const sandRoot = root.querySelector("[data-sci-sand]");
    const platePic = root.querySelector("[data-sci-plate]");
    const cleanPic = root.querySelector("[data-sci-clean]");
    const mapImg = root.querySelector("[data-sci-map]");
    const glCanvas = root.querySelector("[data-sci-gl]");
    const haze = root.querySelector("[data-sci-haze]");
    const motion = window.matchMedia("(prefers-reduced-motion: reduce)");

    const sand = { gl: null, loc: null, w: 0, h: 0, progress: -1 };
    const cleanup = [];
    const timers = new Set();
    let dead = false;
    let scroller = null;
    let frame = 0;
    let awakeUntil = 0;
    let inView = false;
    let current = 0;
    let seeded = false;
    let booted = false;
    let phase = "";

    function on(target, type, fn, opts) {
      target.addEventListener(type, fn, opts);
      cleanup.push(() => target.removeEventListener(type, fn, opts));
    }

    /* Anything the GL path throws — most plausibly a tainted-canvas
       SecurityError from texImage2D if a plate ever resolves to a foreign
       origin — must cost us the shader, not the whole hero. Without this the
       throw escapes create() and the section never mounts: no scrub, no
       countdown, a dead hero that ignores scrolling. */
    function initGL() {
      try {
        return setupGL();
      } catch (err) {
        // eslint-disable-next-line no-console
        console.warn("[sci-landing] WebGL path unavailable, using CSS fallback:", err);
        sand.gl = null;
        return false;
      }
    }

    function setupGL() {
      if (!glCanvas || !platePic || !cleanPic || !mapImg) return false;
      if (!ready(platePic) || !ready(cleanPic) || !ready(mapImg)) return false;
      let gl = null;
      try {
        const opts = {
          alpha: false,
          antialias: false,
          depth: false,
          stencil: false,
          powerPreference: "low-power",
        };
        gl = glCanvas.getContext("webgl", opts) || glCanvas.getContext("experimental-webgl", opts);
      } catch (e) {
        gl = null;
      }
      if (!gl) return false;

      const vs = compile(gl, gl.VERTEX_SHADER, VERT);
      const fs = compile(gl, gl.FRAGMENT_SHADER, FRAG);
      if (!vs || !fs) return false;
      const prog = gl.createProgram();
      gl.attachShader(prog, vs);
      gl.attachShader(prog, fs);
      gl.linkProgram(prog);
      if (!gl.getProgramParameter(prog, gl.LINK_STATUS)) return false;
      gl.useProgram(prog);

      const buf = gl.createBuffer();
      gl.bindBuffer(gl.ARRAY_BUFFER, buf);
      gl.bufferData(gl.ARRAY_BUFFER, new Float32Array([-1, -1, 3, -1, -1, 3]), gl.STATIC_DRAW);
      const aPos = gl.getAttribLocation(prog, "aPos");
      gl.enableVertexAttribArray(aPos);
      gl.vertexAttribPointer(aPos, 2, gl.FLOAT, false, 0, 0);

      gl.pixelStorei(gl.UNPACK_FLIP_Y_WEBGL, false);
      gl.pixelStorei(gl.UNPACK_PREMULTIPLY_ALPHA_WEBGL, false);
      gl.activeTexture(gl.TEXTURE0);
      makeTexture(gl, platePic);
      gl.activeTexture(gl.TEXTURE1);
      makeTexture(gl, cleanPic);
      gl.activeTexture(gl.TEXTURE2);
      makeTexture(gl, mapImg);

      gl.uniform1i(gl.getUniformLocation(prog, "uPlate"), 0);
      gl.uniform1i(gl.getUniformLocation(prog, "uClean"), 1);
      gl.uniform1i(gl.getUniformLocation(prog, "uMap"), 2);
      gl.uniform2f(gl.getUniformLocation(prog, "uCropMin"), CROP.x0, CROP.y0);
      gl.uniform2f(gl.getUniformLocation(prog, "uCropSize"), CROP_W, CROP_H);
      gl.uniform2f(gl.getUniformLocation(prog, "uGrainCells"), 430.0, 494.0);

      sand.gl = gl;
      sand.loc = {
        scale: gl.getUniformLocation(prog, "uScale"),
        offset: gl.getUniformLocation(prog, "uOffset"),
        progress: gl.getUniformLocation(prog, "uProgress"),
        edge: gl.getUniformLocation(prog, "uEdge"),
        drift: gl.getUniformLocation(prog, "uDrift"),
        lift: gl.getUniformLocation(prog, "uLift"),
      };
      return true;
    }

    function sizeCanvas() {
      if (!sand.gl || !glCanvas) return;
      const rect = glCanvas.getBoundingClientRect();
      const cap = window.innerWidth < 760 ? 1.75 : 2;
      const dpr = Math.min(window.devicePixelRatio || 1, cap);
      const w = Math.max(1, Math.round(rect.width * dpr));
      const h = Math.max(1, Math.round(rect.height * dpr));
      if (w === sand.w && h === sand.h) return;
      sand.w = w;
      sand.h = h;
      glCanvas.width = w;
      glCanvas.height = h;
      sand.gl.viewport(0, 0, w, h);
    }

    /* Places the clean crop over the carton for the no-WebGL path, using the
       same cover-fit window the shader samples. */
    function layoutFallback() {
      if (!cleanPic || !sandRoot) return;
      const w = sandRoot.clientWidth;
      const h = sandRoot.clientHeight;
      if (!w || !h) return;
      const v = coverView(w, h, 1);
      cleanPic.style.left = `${((CROP.x0 - v.ox) / v.sx) * w}px`;
      cleanPic.style.top = `${((CROP.y0 - v.oy) / v.sy) * h}px`;
      cleanPic.style.width = `${(CROP_W / v.sx) * w}px`;
      cleanPic.style.height = `${(CROP_H / v.sy) * h}px`;
    }

    function drawSand(p) {
      // 0.00-0.06 hold buried · 0.06-0.62 erode · 0.62-1.00 hold the payoff
      const dissolve = ease(seg(p, 0.06, 0.62));
      const zoom = mix(1, 0.9, easeInOut(seg(p, 0.08, 0.95)));
      if (sand.gl) {
        sizeCanvas();
        const v = coverView(sand.w, sand.h, zoom);
        const gl = sand.gl;
        gl.uniform2f(sand.loc.scale, v.sx, v.sy);
        gl.uniform2f(sand.loc.offset, v.ox, v.oy);
        gl.uniform1f(sand.loc.progress, dissolve);
        // the front tightens from clumps to single grains as the sweep runs
        gl.uniform1f(sand.loc.edge, mix(0.115, 0.055, dissolve));
        gl.uniform1f(sand.loc.drift, 0.006 * Math.sin(Math.PI * dissolve));
        gl.uniform1f(sand.loc.lift, 0.75 * Math.sin(Math.PI * dissolve) + 0.1);
        gl.drawArrays(gl.TRIANGLES, 0, 3);
      } else if (cleanPic) {
        cleanPic.style.setProperty("--sand", String(dissolve));
        cleanPic.style.opacity = String(clamp(dissolve * 3));
      }
      if (haze) {
        haze.style.opacity = String(0.34 * Math.sin(Math.PI * dissolve));
        haze.style.transform = `translate3d(${dissolve * 26}px, ${dissolve * -20}px, 0)`;
      }
      if (cue) cue.style.opacity = String(0.72 * (1 - seg(p, 0.02, 0.16)));
      if (caption) caption.style.color = `rgba(247,245,240,${0.72 * ease(seg(p, 0.58, 0.82))})`;
    }

    function applySand(p) {
      if (!sandRoot) return;
      const v = clamp(p);
      if (sand.progress >= 0 && Math.abs(v - sand.progress) < 0.0004) return;
      sand.progress = v;
      drawSand(v);
    }

    /* ── Countdown ─────────────────────────────────────────────────────── */

    function flipFaces(el) {
      return {
        staticTop: el.querySelector(".sci-flip__static--top .sci-flip__glyph"),
        staticBottom: el.querySelector(".sci-flip__static--bottom .sci-flip__glyph"),
        leafTop: el.querySelector(".sci-flip__leaf--top"),
        leafBottom: el.querySelector(".sci-flip__leaf--bottom"),
        leafTopGlyph: el.querySelector(".sci-flip__leaf--top .sci-flip__glyph"),
        leafBottomGlyph: el.querySelector(".sci-flip__leaf--bottom .sci-flip__glyph"),
      };
    }

    function setFlipValue(faces, value) {
      [faces.staticTop, faces.staticBottom, faces.leafTopGlyph, faces.leafBottomGlyph].forEach((node) => {
        if (node) node.textContent = value;
      });
    }

    function turn(el, from, to, duration, easing) {
      return el.animate(
        [{ transform: `rotateX(${from}deg)` }, { transform: `rotateX(${to}deg)` }],
        { duration, easing, fill: "forwards" }
      );
    }

    function later(fn, ms) {
      const id = window.setTimeout(() => {
        timers.delete(id);
        if (!dead) fn();
      }, ms);
      timers.add(id);
      return id;
    }

    function flipDigit(el, next) {
      if (!el || dead) return;
      const faces = flipFaces(el);
      if (!faces.staticTop || !faces.staticBottom) return;
      if (el.dataset.value === next) return;

      const snap = () => {
        setFlipValue(faces, next);
        el.dataset.value = next;
      };

      if (!el.dataset.value) {
        snap();
        return;
      }
      const canAnimate = CAN_WAAPI && !motion.matches && faces.leafTop && faces.leafBottom;
      if (!canAnimate) {
        snap();
        return;
      }
      if (el.dataset.busy === "1") {
        el.dataset.pending = next;
        return;
      }
      el.dataset.busy = "1";

      const prev = el.dataset.value;
      if (faces.leafTopGlyph) faces.leafTopGlyph.textContent = prev;
      if (faces.leafBottomGlyph) faces.leafBottomGlyph.textContent = next;
      faces.staticTop.textContent = next;
      faces.staticBottom.textContent = prev;
      faces.leafTop.style.zIndex = "5";
      faces.leafBottom.style.zIndex = "4";

      const top = turn(faces.leafTop, 0, -90, 280, FLIP_IN);
      let bottom = null;

      const settle = () => {
        // a forwards-filled animation outranks inline styles, so drop both
        // effects before restoring the resting transforms
        if (top) top.cancel();
        if (bottom) bottom.cancel();
        setFlipValue(faces, next);
        faces.leafTop.style.transform = "rotateX(0deg)";
        faces.leafBottom.style.transform = "rotateX(90deg)";
        faces.leafTop.style.zIndex = "4";
        faces.leafBottom.style.zIndex = "3";
        el.dataset.value = next;
        el.dataset.busy = "0";
        const pending = el.dataset.pending;
        delete el.dataset.pending;
        if (pending && pending !== next) flipDigit(el, pending);
      };

      // the leaves overlap by 40ms so the fall reads as one continuous motion
      later(() => {
        bottom = turn(faces.leafBottom, 90, 0, 300, FLIP_OUT);
        if (bottom) bottom.addEventListener("finish", settle, { once: true });
        else settle();
      }, 240);
    }

    function splitDigits(value, size) {
      return String(Math.max(0, value)).padStart(size, "0").slice(-size).split("");
    }

    /* Two phases off one clock. Before launch the timer runs down to it; after
       launch it runs down the availability window; once that closes it holds at
       all zeros so the row keeps its height instead of vanishing. The phase is
       derived from Date.now() on every tick rather than scheduled once, so a
       page opened after launch starts in the right phase and a page left open
       crosses over without a reload. */
    function countdownPlan(launch, span, now) {
      if (now < launch) return { phase: "pre", left: launch - now };
      const close = launch + span;
      if (now < close) return { phase: "live", left: close - now };
      return { phase: "ended", left: 0 };
    }

    function applyPhase(next) {
      if (next === phase) return;
      phase = next;
      timer.dataset.phase = next;
      const copy = next === "pre" ? timer.dataset.kicker : timer.dataset.kickerLive;
      if (kicker && copy) kicker.textContent = copy;
    }

    function renderCountdown(launch, span) {
      if (!timer || dead) return;
      const plan = countdownPlan(launch, span, Date.now());
      applyPhase(plan.phase);
      const boxes = timer.querySelectorAll("[data-sci-digit]");
      let left = plan.left;
      const days = Math.floor(left / 86400000);
      left -= days * 86400000;
      const hours = Math.floor(left / 3600000);
      left -= hours * 3600000;
      const mins = Math.floor(left / 60000);
      const secs = Math.floor((left / 1000) % 60);
      const digits = [
        ...splitDigits(days, 2),
        ...splitDigits(hours, 2),
        ...splitDigits(mins, 2),
        ...splitDigits(secs, 2),
      ];
      boxes.forEach((box, i) => flipDigit(box, digits[i] || "0"));
    }

    if (timer) {
      const launch = new Date(timer.getAttribute("data-end") || "").getTime();
      const asked = parseFloat(timer.getAttribute("data-duration-days"));
      const span = Math.max(0, Number.isFinite(asked) ? asked : 14) * 86400000;
      if (Number.isFinite(launch)) {
        renderCountdown(launch, span);
        const beat = window.setInterval(() => renderCountdown(launch, span), 1000);
        cleanup.push(() => window.clearInterval(beat));
      }
    }

    /* ── Scroll driver ─────────────────────────────────────────────────── */

    /* The theme editor renders the storefront in an iframe, and themes vary in
       whether the document or a wrapper element is the scrollport. Resolve it
       so progress is measured against whatever actually moves. */
    function findScroller() {
      let el = hero && hero.parentElement;
      while (el && el !== document.body && el !== document.documentElement) {
        const oy = window.getComputedStyle(el).overflowY;
        if ((oy === "auto" || oy === "scroll") && el.scrollHeight > el.clientHeight + 1) return el;
        el = el.parentElement;
      }
      return null;
    }

    function viewportHeight() {
      if (scroller) return scroller.clientHeight;
      return window.innerHeight || document.documentElement.clientHeight || 0;
    }

    /* Rect-based, so it is immune to which element owns the scroll offset and
       to the hero being translated by a sticky/transform ancestor. */
    function rawProgress() {
      if (!hero) return 0;
      const rect = hero.getBoundingClientRect();
      const top = scroller ? rect.top - scroller.getBoundingClientRect().top : rect.top;
      const view = viewportHeight();
      const span = hero.offsetHeight - view;
      if (span > 1) return clamp(-top / span);
      /* Hero shorter than the scrollport (portrait band). It is the first thing
         on the page, so it never enters from below: measuring its whole pass
         would put the sweep at 0.56 before a finger touches the screen. Scrub
         instead from wherever it starts — its own offset, or the bottom edge if
         it ever sits below the fold — and spend the sweep over two fifths of
         its height, which lands the end of the erosion (p 0.62) while the
         carton is still clear of the sticky store header. */
      const start = Math.min(top + (scroller ? scroller.scrollTop : window.scrollY || 0), view);
      const travel = hero.offsetHeight * 0.4;
      if (travel <= 0) return 0;
      return clamp((start - top) / travel);
    }

    function stop() {
      if (frame) {
        window.cancelAnimationFrame(frame);
        frame = 0;
      }
    }

    function tick() {
      frame = 0;
      if (dead) return;
      const target = rawProgress();
      // light smoothing, matching the feel of the old scrubbed timeline
      const delta = target - current;
      current = Math.abs(delta) < 0.0008 ? target : current + delta * 0.18;
      applySand(current);
      const settling = Math.abs(target - current) >= 0.0008;
      if (inView || settling || Date.now() < awakeUntil) {
        frame = window.requestAnimationFrame(tick);
      }
    }

    function wake(ms) {
      if (dead) return;
      awakeUntil = Math.max(awakeUntil, Date.now() + (ms || 700));
      if (!frame) frame = window.requestAnimationFrame(tick);
    }

    function refresh() {
      if (dead) return;
      scroller = findScroller();
      layoutFallback();
      sizeCanvas();
      sand.progress = -1;
      if (!seeded) {
        current = rawProgress();
        seeded = true;
      }
      wake(900);
    }

    /* prefers-reduced-motion: no scrub at all. Hold the uncovered carton so
       the payoff is still delivered, just without the journey. */
    function stillSand() {
      if (sandRoot) sandRoot.classList.add("sci-sand--still");
      if (haze) haze.style.opacity = "0";
      if (cue) cue.style.opacity = "0";
      if (caption) caption.style.color = "rgba(247,245,240,0.72)";
      layoutFallback();
      const paint = () => {
        if (dead || !sandRoot) return;
        if (initGL()) {
          sandRoot.classList.add("sci-sand--gl");
          sizeCanvas();
          drawSand(1);
        } else if (cleanPic) {
          sandRoot.classList.add("sci-sand--fallback");
          cleanPic.style.setProperty("--sand", "1");
          cleanPic.style.opacity = "1";
        }
      };
      whenReady([platePic, cleanPic, mapImg], paint);
      on(window, "resize", () => {
        layoutFallback();
        if (sand.gl) {
          sizeCanvas();
          drawSand(1);
        }
      });
    }

    function bootSand() {
      if (dead || booted || !sandRoot) return;
      booted = true;
      if (initGL()) {
        sandRoot.classList.add("sci-sand--gl");
      } else {
        sandRoot.classList.add("sci-sand--fallback");
      }

      // capture:true also catches scrolls on a wrapper scrollport
      on(window, "scroll", () => wake(), { passive: true, capture: true });
      on(window, "resize", refresh);
      on(window, "orientationchange", refresh);
      on(window, "load", refresh);

      if (typeof IntersectionObserver === "function") {
        const io = new IntersectionObserver(
          (entries) => {
            inView = entries.some((entry) => entry.isIntersecting);
            wake();
          },
          { rootMargin: "25% 0px 25% 0px", threshold: 0 }
        );
        io.observe(hero);
        cleanup.push(() => io.disconnect());
      } else {
        inView = true;
      }

      if (typeof ResizeObserver === "function") {
        const ro = new ResizeObserver(() => refresh());
        ro.observe(hero);
        cleanup.push(() => ro.disconnect());
      }

      refresh();
    }

    if (!hero || !sandRoot || motion.matches) {
      stillSand();
    } else {
      // The covered plate is already painted underneath, so nothing is blank
      // while the three plates decode.
      whenReady([platePic, cleanPic, mapImg], bootSand);
      // Cover the case where a plate never resolves: still scrub the overlay
      // chrome rather than leaving a dead hero.
      later(bootSand, 4000);
    }

    return {
      refresh,
      wake,
      // exposed so the scrub can be inspected from the console
      get progress() {
        return sand.progress;
      },
      destroy() {
        dead = true;
        stop();
        timers.forEach((id) => window.clearTimeout(id));
        timers.clear();
        cleanup.forEach((fn) => fn());
        cleanup.length = 0;
        if (sand.gl) {
          const lose = sand.gl.getExtension("WEBGL_lose_context");
          if (lose) lose.loseContext();
          sand.gl = null;
        }
      },
    };
  }

  /* ── Mount / unmount ─────────────────────────────────────────────────── */

  function roots(scope) {
    const out = [];
    const node = scope && scope.nodeType === 1 ? scope : document;
    if (node !== document && node.matches("[data-sci-landing]")) out.push(node);
    node.querySelectorAll("[data-sci-landing]").forEach((el) => out.push(el));
    return out;
  }

  function mount(scope) {
    roots(scope).forEach((root) => {
      if (NS.live.has(root)) return;
      try {
        NS.live.set(root, create(root));
      } catch (err) {
        // eslint-disable-next-line no-console
        console.error("[sci-landing] init failed", err);
      }
    });
  }

  function unmount(scope) {
    const node = scope && scope.nodeType === 1 ? scope : null;
    NS.live.forEach((inst, root) => {
      const detached = !root.isConnected;
      const inScope = node ? node === root || node.contains(root) : true;
      if (!detached && !inScope) return;
      inst.destroy();
      NS.live.delete(root);
    });
  }

  function refreshAll() {
    NS.live.forEach((inst) => inst.refresh());
  }

  if (!NS.wired) {
    NS.wired = true;
    // Shopify editor lifecycle: sections are swapped into a live document, so
    // re-mount from the event target instead of trusting DOMContentLoaded.
    document.addEventListener("shopify:section:load", (event) => {
      unmount(event.target);
      mount(event.target);
    });
    document.addEventListener("shopify:section:unload", (event) => unmount(event.target));
    document.addEventListener("shopify:section:select", refreshAll);
    document.addEventListener("shopify:section:deselect", refreshAll);
    document.addEventListener("shopify:section:reorder", refreshAll);
    document.addEventListener("shopify:block:select", refreshAll);
    document.addEventListener("shopify:block:deselect", refreshAll);
  }

  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", () => mount(document), { once: true });
  } else {
    mount(document);
  }
})();
